import React from "react"
import {
  STUDY_SLOTS,
  WEEK_DAYS,
  buildBehaviour,
  buildTrends,
  clockTime,
  sinceDays,
} from "../learnerReportModel"
import type { RetryVerdict, TrendRow } from "../learnerReportModel"
import { EmptyNote, PillGroup, SUBJECT_FILTERS, SubjectChip } from "../reportUi"
import type { SubjectFilter } from "../reportUi"
import type { ReportView } from "./reportView"

function signed(n: number): string {
  return `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toFixed(1)}`
}

function points(values: number[]): string {
  const step = values.length > 1 ? 200 / (values.length - 1) : 0
  return values
    .map((v, i) => `${(i * step).toFixed(1)},${(4 + ((10 - v) / 10) * 48).toFixed(1)}`)
    .join(" ")
}

const VERDICT_COLORS: Record<RetryVerdict, [string, string]> = {
  Learning: ["#E6F5EC", "#1E6E40"],
  "Slow gain": ["#FFF2DE", "#8A5300"],
  Stalled: ["#FDE6E9", "#A8243A"],
  Guessing: ["#FDE6E9", "#A8243A"],
}

function shade(n: number): [string, string] {
  if (n === 0) return ["#F1F3F8", "#8C93A3"]
  if (n === 1) return ["#C9D2EA", "#1C2333"]
  if (n === 2) return ["#8EA0D3", "#1C2333"]
  if (n === 3) return ["#3D55A8", "#FFFFFF"]
  return ["#1F2F6B", "#FFFFFF"]
}

const Highlight = ({
  label,
  row,
  bg,
  border,
  fg,
}: {
  label: string
  row: TrendRow | null
  bg: string
  border: string
  fg: string
}) => (
  <div className="sr-trend-hl" style={{ background: bg, border: `1px solid ${border}` }}>
    <span className="sr-trend-hl-label" style={{ color: fg }}>{label}</span>
    <span style={{ fontSize: 16, fontWeight: 700 }}>{row ? row.name : "–"}</span>
    <span style={{ fontSize: 13, color: "#4A5163" }}>
      {row ? `${row.first.toFixed(1)} → ${row.cur.toFixed(1)} (${signed(row.diff)}) over 8 weeks` : "No change to report"}
    </span>
  </div>
)

const AnalyticsTab: React.FC<{ view: ReportView }> = ({ view }) => {
  const [subject, setSubject] = React.useState<SubjectFilter>("both")

  const groups = React.useMemo(
    () => view.groups.filter(g => subject === "both" || g.subject === subject),
    [view.groups, subject]
  )
  const trends = React.useMemo(() => buildTrends(groups, view.now), [groups, view.now])
  const behaviour = React.useMemo(() => {
    const since = sinceDays(view.now, 30)
    return buildBehaviour(groups.filter(g => g.last >= since))
  }, [groups, view.now])

  const sorted = [...trends].sort((a, b) => a.diff - b.diff)
  const fall = sorted[0] && sorted[0].dir === "down" ? sorted[0] : null
  const last = sorted[sorted.length - 1]
  const rise = last && last.dir === "up" ? last : null
  const count = (d: TrendRow["dir"]) => trends.filter(t => t.dir === d).length

  return (
    <>
      <div className="sr-head-center">
        <PillGroup options={SUBJECT_FILTERS} value={subject} onChange={setSubject} label="Subject" />
        <span className="sr-muted">Last 8 weeks · weekly skill score (0–10)</span>
      </div>

      <section aria-label="Skill trends" className="sr-card sr-section" style={{ gap: 18 }}>
        <div className="sr-stack-4">
          <h2 className="sr-h2">Skill trends</h2>
          <span className="sr-muted">A falling skill needs attention before a low but rising one.</span>
        </div>
        {trends.length === 0 ? (
          <EmptyNote>Not enough scored activity yet to show trends.</EmptyNote>
        ) : (
          <>
            <div className="sr-auto-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              <Highlight label="BIGGEST DROP" row={fall} bg="#FDF0F2" border="#F4C1C9" fg="#A8243A" />
              <Highlight label="FASTEST RISE" row={rise} bg="#EEF8F2" border="#BFE3CC" fg="#1E6E40" />
              <div className="sr-trend-hl" style={{ background: "#F6F7FB", border: "1px solid #E3E6EE" }}>
                <span className="sr-trend-hl-label" style={{ color: "#4A5163" }}>DIRECTION</span>
                <span style={{ fontSize: 16, fontWeight: 700 }}>
                  {count("up")} rising · {count("down")} falling
                </span>
                <span style={{ fontSize: 13, color: "#4A5163" }}>{count("flat")} steady (change under 0.3)</span>
              </div>
            </div>
            <div className="sr-auto-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
              {trends.map(t => {
                const color = t.dir === "up" ? "#1E6E40" : t.dir === "down" ? "#A8243A" : "#4A5163"
                const arrow =
                  t.dir === "up" ? "M12 19V5M5 12l7-7 7 7" : t.dir === "down" ? "M12 5v14M5 12l7 7 7-7" : "M5 12h14"
                return (
                  <div key={`${t.subject}|${t.name}`} className="sr-trend-card">
                    <div className="sr-head-center" style={{ gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</span>
                      <SubjectChip subject={t.subject} small />
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                      <span className="sr-mono" style={{ fontSize: 24, fontWeight: 700 }}>{t.cur.toFixed(1)}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 13, fontWeight: 700, color }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d={arrow} />
                        </svg>
                        {signed(t.diff)}
                      </span>
                    </div>
                    <svg
                      width="100%"
                      height="56"
                      viewBox="0 0 200 56"
                      preserveAspectRatio="none"
                      role="img"
                      aria-label={`${t.name} went from ${t.first.toFixed(1)} to ${t.cur.toFixed(1)} over 8 weeks`}
                    >
                      <line x1="0" x2="200" y1="8.8" y2="8.8" stroke="#2E9D5E" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
                      <line x1="0" x2="200" y1="52" y2="52" stroke="#E3E6EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                      <polyline points={points(t.values)} fill="none" stroke="#1F2F6B" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
                    </svg>
                    <div className="sr-trend-foot">
                      <span>8 wks ago · {t.first.toFixed(1)}</span>
                      <span>Pass line 9.0</span>
                      <span>Now</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </section>

      <section aria-label="Attempt behaviour" className="sr-card sr-section" style={{ gap: 18 }}>
        <div className="sr-stack-4">
          <h2 className="sr-h2">How {view.firstName} attempts work</h2>
          <span className="sr-muted">From the timestamp of every attempt, last 30 days</span>
        </div>
        <div className="sr-auto-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          <div className="sr-stat-box">
            <span className="sr-muted">Attempts per activity</span>
            <span className="sr-stat-box-value">{behaviour.perActivity}</span>
            <span className="sr-muted-12">{behaviour.attempts} attempts · {behaviour.activities} activities</span>
          </div>
          <div className="sr-stat-box">
            <span className="sr-muted">Rapid retries</span>
            <span className="sr-stat-box-value">{behaviour.rapid}</span>
            <span className="sr-muted-12">
              {behaviour.rapid ? "Possible guessing on these activities" : "No guessing pattern found"}
            </span>
          </div>
          <div className="sr-stat-box">
            <span className="sr-muted">Score gain per retry</span>
            <span className="sr-stat-box-value">{behaviour.gain}</span>
            <span className="sr-muted-12">Points per extra try</span>
          </div>
          <div className="sr-stat-box">
            <span className="sr-muted">Most active</span>
            <span className="sr-stat-box-value">{behaviour.mostActiveDay}</span>
            <span className="sr-muted-12">{behaviour.mostActiveSlot}</span>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
          <div className="sr-stack-12" style={{ flex: "1 1 440px", minWidth: 0 }}>
            <h3 className="sr-h3" style={{ fontSize: 15 }}>Retries: learning or guessing?</h3>
            {behaviour.ladders.length === 0 ? (
              <span className="sr-text-sub">No repeated attempts in the last 30 days.</span>
            ) : (
              behaviour.ladders.map(l => {
                const [bg, fg] = l.verdict ? VERDICT_COLORS[l.verdict] : ["#EEF0F5", "#4A5163"]
                return (
                  <div key={l.key} className="sr-ladder">
                    <div className="sr-head-center" style={{ gap: 8 }}>
                      <span style={{ fontSize: 14, fontWeight: 600 }}>{l.title}</span>
                      {l.verdict ? (
                        <span className="sr-status-chip" style={{ background: bg, color: fg, minWidth: 0 }}>
                          {l.verdict}
                          {l.gainPerTry !== null ? ` · ${signed(l.gainPerTry)} per try` : ""}
                        </span>
                      ) : null}
                    </div>
                    <div className="sr-stack-6">
                      {l.tries.map((t, i) => (
                        <div key={i} className="sr-ladder-try">
                          <span style={{ width: 92, color: "#5E6577" }}>Try {i + 1} · {clockTime(t.at)}</span>
                          <div className="sr-ladder-track">
                            <div style={{ width: `${t.score ?? 0}%` }} />
                          </div>
                          <span className="sr-mono" style={{ width: 36, textAlign: "right", fontWeight: 700 }}>
                            {t.score === null ? "–" : Math.round(t.score)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {l.note ? <span style={{ fontSize: 13, color: "#4A5163" }}>{l.note}</span> : null}
                  </div>
                )
              })
            )}
          </div>

          <div className="sr-stack-12" style={{ flex: "1 1 400px", minWidth: 0 }}>
            <h3 className="sr-h3" style={{ fontSize: 15 }}>When {view.firstName} studies</h3>
            <div style={{ overflowX: "auto" }}>
              <div className="sr-study-grid">
                <span />
                {WEEK_DAYS.map(d => (
                  <span key={d} className="sr-study-day">{d}</span>
                ))}
                {STUDY_SLOTS.map((slot, si) => (
                  <React.Fragment key={slot}>
                    <span className="sr-study-slot">{slot}</span>
                    {behaviour.grid[si].map((n, di) => {
                      const [bg, fg] = shade(n)
                      return (
                        <span
                          key={di}
                          className="sr-study-cell"
                          style={{ background: bg, color: fg }}
                          title={`${WEEK_DAYS[di]} ${slot.toLowerCase()}: ${n} ${n === 1 ? "session" : "sessions"}`}
                        >
                          {n ? n : ""}
                        </span>
                      )
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="sr-study-legend">
              <span>Fewer</span>
              <span style={{ background: "#F1F3F8", border: "1px solid #E3E6EE" }} />
              <span style={{ background: "#C9D2EA" }} />
              <span style={{ background: "#8EA0D3" }} />
              <span style={{ background: "#3D55A8" }} />
              <span style={{ background: "#1F2F6B" }} />
              <span>More sessions</span>
            </div>
            <span style={{ fontSize: 13, color: "#4A5163" }}>{behaviour.summary}</span>
          </div>
        </div>

        <details className="sr-flags">
          <summary>How these flags are worked out</summary>
          <div className="sr-auto-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10, marginTop: 10 }}>
            <span><b>Learning:</b> each retry adds 10 or more points.</span>
            <span><b>Slow gain:</b> each retry adds 5 to 9.9 points.</span>
            <span><b>Stalled:</b> retries adding under 5 points each.</span>
            <span><b>Guessing:</b> 3+ tries within 5 minutes with almost no gain.</span>
          </div>
        </details>
      </section>
    </>
  )
}

export default AnalyticsTab
